'use client'

import React, { useState, useEffect, Suspense } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { signInWithEmail, resetPasswordForEmail } from '@/lib/auth/client'
import { toast } from 'react-hot-toast'
import { Eye, EyeOff, KeyRound } from 'lucide-react'
import { Mail01 as Mail, Lock01 as Lock } from '@untitledui/icons'
import { Input } from '@/components/base/input/input'
import Image from 'next/image'
import { Button } from '@/components/ui/button'
import { useBranding } from '@/contexts/BrandingContext'

// PurpleTree brand color - used inline to prevent flash of unstyled content
const BRAND_PURPLE = '#6226C4'

function SignInForm() {
  const { branding, loading: brandingLoading } = useBranding()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()

  useEffect(() => {
    // Check for error messages in URL
    const error = searchParams.get('error')
    if (error === 'auth_failed') {
      toast.error('Authentication failed. Please try again.')
    }
  }, [searchParams])

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const { data, error } = await signInWithEmail(email, password)
      
      if (error) {
        throw error
      }

      toast.success('Successfully signed in!')
      
      // Track login activity
      try {
        await fetch('/api/auth/track-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            event: 'login',
            userId: data?.user?.id || email
          })
        })
      } catch (trackError) {
        // Don't fail login if tracking fails
      }
      
      // Small delay to ensure session is established
      setTimeout(() => {
        router.push('/dashboard')
      }, 100)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to sign in')
    } finally {
      setLoading(false)
    }
  }


  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setResetLoading(true)

    try {
      const { error } = await resetPasswordForEmail(resetEmail)

      if (error) {
        throw error
      }

      toast.success('Password reset email sent! Check your inbox.')
      setShowForgotPassword(false)
      setResetEmail('')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to send reset email')
    } finally {
      setResetLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: BRAND_PURPLE }}
    >
      {/* Login Card - explicit white background to prevent transparency */}
      <div
        className="login-card w-full max-w-md rounded-2xl shadow-2xl p-8 bg-white isolate"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-56 mx-auto mb-6 relative h-16">
            <Image
              src={branding.companyLogoUrl}
              alt={branding.companyName}
              fill
              className="object-contain"
              priority
              unoptimized
            />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">Welcome back</h1>
          <p className="text-gray-500 mt-1 text-sm">Sign in to your account</p>
        </div>

        {/* Email/Password Form */}
        <form onSubmit={handleEmailSignIn} className="space-y-5">
          <Input
            label="Email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(value) => setEmail(value)}
            icon={Mail}
            required
          />

          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <label className="text-sm font-medium text-gray-900">Password</label>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-xs text-purple-600 hover:text-purple-700 font-medium"
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(value) => setPassword(value)}
                icon={Lock}
                required
                inputClassName="pr-10"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-11 text-base font-medium"
            style={{ backgroundColor: BRAND_PURPLE }}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        {/* Footer */}
        <p className="text-center mt-6 text-xs text-gray-400">
          Need access? Contact your administrator
        </p>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="login-card w-full max-w-md rounded-2xl shadow-2xl p-6">
            <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-900">
              <KeyRound className="h-5 w-5" style={{ color: BRAND_PURPLE }} />
              Reset Password
            </h3>
            <p className="py-4 text-gray-500 text-sm">
              Enter your email address and we&apos;ll send you a link to reset your password.
            </p>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <Input
                label="Email"
                type="email"
                placeholder="Enter your email"
                value={resetEmail}
                onChange={(value) => setResetEmail(value)}
                icon={Mail}
                required
              />

              <div className="flex gap-3 justify-end pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForgotPassword(false)
                    setResetEmail('')
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={resetLoading}
                  style={{ backgroundColor: BRAND_PURPLE }}
                >
                  {resetLoading ? 'Sending...' : 'Send Reset Email'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

// Loading fallback with matching purple background
function LoadingFallback() {
  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: BRAND_PURPLE }}
    >
      <div
        className="login-card w-full max-w-md rounded-2xl shadow-2xl p-8 flex flex-col items-center justify-center"
        style={{ minHeight: '400px' }}
      >
        <div className="w-12 h-12 border-4 border-gray-200 border-t-purple-600 rounded-full animate-spin mb-4" />
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    </div>
  )
}

export default function SignInPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <SignInForm />
    </Suspense>
  )
}